import React, { useEffect, useRef, useState } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import BubbleWrapper from './BubbleWrapper'
import palette from 'core-system/Themes/palette'
import { CircularProgress } from '@mui/material'
import Text from 'core-system/Text'
import {
  fetchRoute,
  createMarker,
  createMap,
  createPolyline,
  createInfoWindow,
  polylineColor,
  polylineOpacity,
  createCircularMarkersForLegs,
  getLineColor,
} from '../google-maps/googleMapsFunctions'
import JourneyPlanner from '../google-maps/JourneyPlanner'
import { ChatTypes } from '../types'
import SwiftlyLogo from './Swiftly.svg'

const useStyles = makeStyles(() => ({
  mapContainer: {
    padding: '12px',
  },
  mapInnerContainer: {
    borderRadius: '8px',
    overflow: 'hidden',
  },
  map: {
    width: '100%',
    height: '450px',
    '& iframe': {
      // This was preventing the map from being clickable
      display: 'none',
    },
    '& .gm-style-mtc-bbw': {
      // This was preventing the map from being clickable
      zIndex: '1000000 !important',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 400,
  },
  markerContainer: {
    marginRight: 24,
    marginBottom: 12,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
}))

interface MapBubbleProps {
  chatId: string
  chatIsInView: (chat: ChatTypes) => void
  showLike: boolean
  onLikeClick: () => void
  isLiked: boolean
  mapData: any
  mapApiLoaded: boolean
}

/*
  mapData: {
  starting_destination: { latitude: number; longitude: number },
  ending_destination: { latitude: number; longitude: number },
  departureTime: string
  } for route
  mapData: { latitude: number; longitude: number } for marker
*/
const MapBubble = ({
  chatId = '',
  chatIsInView = () => {},
  showLike = false,
  onLikeClick = () => {},
  isLiked = false,
  mapData,
  mapApiLoaded = false,
}: MapBubbleProps) => {
  const classes = useStyles()
  const mapRef = useRef(null)
  const [infoWindows, setInfoWindows] = useState<google.maps.InfoWindow[]>([])
  const [stepsPolyLines, setStepsPolyLines] = useState<google.maps.Polyline[]>(
    []
  )
  const [journeyPlannerSteps, setJourneyPlannerSteps] = useState<any[]>([])
  const mapType = mapData.departure_time ? 'route' : 'marker'

  useEffect(() => {
    if (!mapApiLoaded || !mapRef.current) return

    if (mapType === 'marker') {
      const map = createMap(mapRef, chatId, mapType, [mapData])
      createMarker(map, mapData)
    }

    if (mapType === 'route') {
      renderRoutes()
    }
  }, [mapData, mapApiLoaded])

  const renderRoutes = async () => {
    const startingDestinationLatLong = mapData.starting_destination
    const endingDestinationLatLong = mapData.ending_destination
    // If we are not able to geocode the address
    // We will render a message on map ref instead of the map
    if (!startingDestinationLatLong || !endingDestinationLatLong) {
      mapRef.current.innerHTML = `
        <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; height: 100%; width: 100%;">
          <p><b>We are unable to find the address you provided.</b></p>
          <p>Please try again with detailed address.</p>
        </div>
      `
      return
    }

    const map = createMap(mapRef, chatId, mapType, [
      startingDestinationLatLong,
      endingDestinationLatLong,
    ])
    const bounds = new window.google.maps.LatLngBounds()
    bounds.extend(
      new window.google.maps.LatLng(
        startingDestinationLatLong.latitude,
        startingDestinationLatLong.longitude
      )
    )
    bounds.extend(
      new window.google.maps.LatLng(
        endingDestinationLatLong.latitude,
        endingDestinationLatLong.longitude
      )
    )
    map.fitBounds(bounds)
    displayRoutes(
      map,
      startingDestinationLatLong,
      endingDestinationLatLong,
      mapData.departure_time
    )
  }

  const displayRoutes = async (
    map: google.maps.Map,
    startingDestinationLatLong: { latitude: number; longitude: number },
    endingDestinationLatLong: { latitude: number; longitude: number },
    departureTime: string
  ) => {
    const routes = await fetchRoute(
      startingDestinationLatLong,
      endingDestinationLatLong,
      departureTime
    )

    const allPolyLines = []
    const allInfoWindows = []
    const allStepsPolyLines = []
    routes.forEach((route, routesIndex) => {
      const { transitSteps, polyline } = route

      const decodedPathPolyline =
        window.google.maps.geometry.encoding.decodePath(
          polyline.encodedPolyline
        )

      // create info window
      const infoWindow = createInfoWindow(route.distance, route.duration)

      // create markers
      createMarker(map, {
        latitude: decodedPathPolyline[0].lat(),
        longitude: decodedPathPolyline[0].lng(),
      })
      createMarker(map, {
        latitude: decodedPathPolyline[decodedPathPolyline.length - 1].lat(),
        longitude: decodedPathPolyline[decodedPathPolyline.length - 1].lng(),
      })

      // open info window on the first route
      if (routesIndex === 0) {
        infoWindow.setPosition(
          decodedPathPolyline[Math.floor(decodedPathPolyline.length / 2)]
        )
        infoWindow.open(map)
        // adding the index 0 into current polylines
        setJourneyPlannerSteps(transitSteps)
      }

      allInfoWindows.push(infoWindow)
      allPolyLines.push(polyline)

      if (transitSteps && transitSteps.length > 0) {
        transitSteps.forEach((step) => {
          const decodedPath = window.google.maps.geometry.encoding.decodePath(
            step.polyline.encodedPolyline
          )

          // create circular markers for the legs
          createCircularMarkersForLegs(map, step.startLocation)

          // create polyline
          const stepPolyline = createPolyline(
            map,
            decodedPath,
            routesIndex === 0,
            (event: google.maps.KmlMouseEvent) =>
              onPolylineClick(event, infoWindow, map, routesIndex),
            step.travelMode,
            getLineColor(step.transitDetails)
          )
          allStepsPolyLines.push({
            ...step,
            polyline: stepPolyline,
            routeIndex: routesIndex,
            travelMode: step.travelMode,
            transitLineColor: getLineColor(step.transitDetails),
          })
        })
      }
    })
    setInfoWindows(allInfoWindows)
    setStepsPolyLines(allStepsPolyLines)
  }

  const onPolylineClick = (
    event: google.maps.KmlMouseEvent,
    infoWindow: google.maps.InfoWindow,
    map: google.maps.Map,
    routeIndex: number
  ) => {
    infoWindow.setPosition(event.latLng)
    infoWindow.open(map)

    const allActiveSteps = []

    // Use functional updates to ensure the latest state is used
    // change every other polyline to grey
    // Getting all the steps polyline for this route
    setStepsPolyLines((stepsPolyLines) => {
      stepsPolyLines.forEach((stepPolyline) => {
        if (stepPolyline.routeIndex !== routeIndex) {
          stepPolyline.polyline.setOptions({
            strokeColor: polylineColor(
              stepPolyline.travelMode,
              false,
              stepPolyline.transitLineColor
            ),
            strokeOpacity: polylineOpacity(stepPolyline.travelMode, false),
          })
        } else {
          allActiveSteps.push(stepPolyline)
          stepPolyline.polyline.setOptions({
            strokeColor: polylineColor(
              stepPolyline.travelMode,
              true,
              stepPolyline.transitLineColor
            ),
            strokeOpacity: polylineOpacity(stepPolyline.travelMode, true),
            zIndex: 1000,
          })
        }
      })
      return stepsPolyLines
    })

    // close every other info window
    setInfoWindows((currentInfoWindows) => {
      currentInfoWindows.forEach((infoWindow, thisIndex) => {
        if (thisIndex !== routeIndex) {
          infoWindow.close()
        }
      })
      return currentInfoWindows
    })

    setJourneyPlannerSteps(allActiveSteps)
  }

  if (!mapApiLoaded) {
    return (
      <BubbleWrapper
        bubbleType='partner'
        chatId={chatId}
        chatIsInView={chatIsInView}
        showLike={showLike}
        onLikeClick={onLikeClick}
        isLiked={isLiked}
        width='full'
      >
        <div className={classes.loadingContainer}>
          <CircularProgress sx={{ color: palette.primary.pink800 }} />
        </div>
      </BubbleWrapper>
    )
  }

  return (
    <BubbleWrapper
      bubbleType='partner'
      chatId={chatId}
      chatIsInView={chatIsInView}
      showLike={showLike}
      onLikeClick={onLikeClick}
      isLiked={isLiked}
      width='full'
    >
      <div className={classes.mapContainer}>
        <div className={classes.mapInnerContainer}>
          <div ref={mapRef} className={classes.map} />
        </div>
      </div>
      {mapType === 'route' && <JourneyPlanner steps={journeyPlannerSteps} />}
      {mapType === 'marker' && (
        <div className={classes.markerContainer}>
          <Text variant='action4' color='text.secondary' marginRight='0.5rem'>
            Powered by
          </Text>
          <img src={SwiftlyLogo} alt='Swiftly' style={{ height: '28px' }} />
        </div>
      )}
    </BubbleWrapper>
  )
}

export default React.memo(MapBubble)
